import React, { useState } from "react";
import {
  Typography,
  Box,
  Button,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useTranslation } from "react-i18next";
import "../../App.css";
import { BookingApi } from "../../API/Apis/BookingApi";
import WebCheckInLayout from "./WebCheckInLayout";
import BookingGetters from "../../store/onlineBooking/getters";

type Props = {
  details: any;
  reservationDetails: any;
  selectedPrivacyPolicy?: any;
  onBack: () => void;
  onNext?: (selectedPolicyIds: number[]) => void;
  isSmallScreen: boolean;
  progress: any;
  isShareboxEnabled?: boolean;
};

const styles = {
  textField: {
    fontWeight: 400,
    fontStyle: "normal",
    fontSize: "14px",
    lineHeight: "18px",
    letterSpacing: "0px",
    color: "#19344F",
    opacity: 1,
    marginTop: "4px",
  },
};

export default function PrivacyPolicyForm({
  details,
  reservationDetails,
  selectedPrivacyPolicy,
  onBack,
  onNext,
  isSmallScreen,
  progress,
  isShareboxEnabled,
}: Props) {
  if (!details) return null;
  const policies = (details[0] || []).sort((a: any, b: any) => a.id - b.id);

  const { t: translate, i18n } = useTranslation();

  const initialChecked = React.useMemo(
    () =>
      policies.reduce((acc: Record<number, boolean>, policy: any) => {
        acc[policy.id] =
          (selectedPrivacyPolicy &&
            selectedPrivacyPolicy.includes(policy.id)) ||
          policy.isDefaultAccepted;
        return acc;
      }, {}),
    [policies, selectedPrivacyPolicy]
  );

  const [checked, setChecked] =
    useState<Record<number, boolean>>(initialChecked);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const id = Number(e.target.name);
    setChecked({
      ...checked,
      [id]: e.target.checked,
    });
  };

  const handleContinue = async () => {
    const selectedPolicyIds = Object.keys(checked)
      .filter((id) => checked[Number(id)])
      .map((id) => Number(id));

    const initialPolicyIds = Object.keys(initialChecked)
      .filter((id) => initialChecked[Number(id)])
      .map((id) => Number(id));

    const changed =
      selectedPolicyIds.length !== initialPolicyIds.length ||
      selectedPolicyIds.some((id) => !initialPolicyIds.includes(id));

    if (onNext) onNext(selectedPolicyIds);

    if (changed) {
      try {
        await BookingApi.savePrivacyPolicy(
          reservationDetails.reservationId,
          selectedPolicyIds
        );
      } catch (error) {
        console.error("Error saving privacy policy:", error);
      }
    }
  };
  const appSettings = BookingGetters.getAppSettings();
  const webCheckinSecondaryColor = appSettings?.webCheckinSecondaryColor;
  return (
    <WebCheckInLayout
      title={translate("PRIVACY POLICY")}
      progress={progress}
      onBack={onBack}
    >
      {policies.map((policy: any) => {
        const text =
          i18n.language === "it"
            ? policy.privacyPolicyLocalLang
            : policy.privacyPolicyEn;

        return (
          <FormControlLabel
            key={policy.id}
            sx={{
              mt: isSmallScreen ? 1 : 3,
              mb: isSmallScreen ? 1 : 2,
              alignItems: "flex-start",
            }}
            control={
              <Checkbox
                checked={checked[policy.id] || false}
                onChange={handleChange}
                name={String(policy.id)}
                icon={<RadioButtonUncheckedIcon />}
                checkedIcon={<CheckCircleIcon />}
                disableRipple
                sx={{
                  color: webCheckinSecondaryColor || "#ff238c",
                  "&.Mui-checked": {
                    color: webCheckinSecondaryColor || "#ff238c",
                  },
                  mt: 0,
                  alignSelf: "flex-start",
                  p: 0.5,
                  paddingRight: 1.5,
                }}
              />
            }
            label={
              <Typography variant="body2" sx={styles.textField}>
                {text}{" "}
                {policy.isMandatory && (
                  <span style={{ color: "red" }}> * (Mandatory)</span>
                )}
              </Typography>
            }
          />
        );
      })}

      <Box display="flex" justifyContent="center">
        <Button
          variant="contained"
          onClick={handleContinue}
          className="webCheckInButton"
          style={{
            borderRadius: `${appSettings?.buttonBorderRadius || 55}px`,
          }}
          sx={
            isShareboxEnabled
              ? {
                  width: "100% !important",
                  maxWidth: "none !important",
                  borderRadius: "10px !important",
                  border: `2px solid ${
                    appSettings?.webCheckinButtonColor || "#19344FCC"
                  } !important`,
                }
              : undefined
          }
        >
          {translate("Continue")}
        </Button>
      </Box>
    </WebCheckInLayout>
  );
}
